import { Player } from '../types/player';
import { Pairing } from '../types/match';

/**
 * 配对算法类
 * 负责处理玩家的随机配对逻辑
 */
export class PairingAlgorithm {
  /**
   * 生成随机配对
   * @param players 参赛玩家数组（必须是4个人）
   * @returns 配对结果
   */
  static generateRandomPairing(players: Player[]): Pairing {
    if (players.length !== 4) {
      throw new Error(`需要恰好4个人进行配对，当前有${players.length}个人`);
    }

    // 验证玩家数据
    this.validatePlayers(players);

    // 随机打乱数组
    const shuffled = this.shuffleArray([...players]);

    return {
      team1: [shuffled[0], shuffled[1]] as [Player, Player],
      team2: [shuffled[2], shuffled[3]] as [Player, Player],
      createdAt: new Date(),
    };
  }

  /**
   * 生成多个随机配对选项
   * @param players 参赛玩家数组
   * @param count 生成配对的数量
   * @returns 多个配对选项
   */
  static generateMultiplePairings(players: Player[], count: number = 3): Pairing[] {
    const pairings: Pairing[] = [];
    const maxAttempts = count * 10; // 防止无限循环
    let attempts = 0;

    while (pairings.length < count && attempts < maxAttempts) {
      const pairing = this.generateRandomPairing(players);
      
      // 检查是否与已有配对重复
      if (!this.isDuplicatePairing(pairing, pairings)) {
        pairings.push(pairing);
      }
      
      attempts++;
    }

    return pairings;
  }

  /**
   * 基于历史记录生成平衡配对
   * @param players 参赛玩家数组
   * @param history 历史配对记录
   * @returns 平衡的配对结果
   */
  static generateBalancedPairing(players: Player[], history: Pairing[] = []): Pairing {
    if (players.length !== 4) {
      throw new Error(`需要恰好4个人进行配对，当前有${players.length}个人`);
    }

    // 如果没有历史记录，直接随机配对
    if (history.length === 0) {
      return this.generateRandomPairing(players);
    }

    // 计算每个配对组合的出现频率
    const pairingFrequency = this.calculatePairingFrequency(players, history);
    
    // 生成所有可能的配对组合
    const allPossiblePairings = this.generateAllPossiblePairings(players);
    
    // 选择出现频率最低的配对
    const bestPairing = allPossiblePairings.reduce((best, current) => {
      const currentScore = this.calculatePairingScore(current, pairingFrequency);
      const bestScore = this.calculatePairingScore(best, pairingFrequency);
      
      return currentScore < bestScore ? current : best;
    });

    return bestPairing;
  }

  /**
   * 验证配对的有效性
   * @param pairing 配对
   * @returns 验证结果
   */
  static validatePairing(pairing: Pairing): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // 检查每队是否有2个人
    if (pairing.team1.length !== 2) {
      errors.push('队伍1必须有2个人');
    }
    if (pairing.team2.length !== 2) {
      errors.push('队伍2必须有2个人');
    }

    // 检查是否有重复的玩家
    const allPlayers = [...pairing.team1, ...pairing.team2];
    const playerIds = allPlayers.map(p => p.id);
    const uniqueIds = new Set(playerIds);
    
    if (playerIds.length !== uniqueIds.size) {
      errors.push('配对中存在重复的玩家');
    }

    // 检查是否恰好有4个人
    if (allPlayers.length !== 4) {
      errors.push('配对必须包含恰好4个玩家');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * 获取配对的字符串表示（用于比较）
   * @param pairing 配对
   * @returns 配对的字符串表示
   */
  static getPairingSignature(pairing: Pairing): string {
    const team1Ids = pairing.team1.map(p => p.id).sort();
    const team2Ids = pairing.team2.map(p => p.id).sort();
    
    // 确保team1和team2的顺序一致（较小的team在前）
    const [firstTeam, secondTeam] = team1Ids.join(',') < team2Ids.join(',') 
      ? [team1Ids, team2Ids] 
      : [team2Ids, team1Ids];
    
    return `${firstTeam.join(',')}|${secondTeam.join(',')}`;
  }

  // 私有辅助方法

  /**
   * 随机打乱数组
   */
  private static shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * 验证玩家数据
   */
  private static validatePlayers(players: Player[]): void {
    // 检查是否有重复的玩家
    const playerIds = players.map(p => p.id);
    const uniqueIds = new Set(playerIds);
    
    if (playerIds.length !== uniqueIds.size) {
      throw new Error('参赛玩家中存在重复');
    }

    // 检查玩家是否都是活跃状态
    const inactivePlayers = players.filter(p => !p.isActive);
    if (inactivePlayers.length > 0) {
      throw new Error(`存在非活跃玩家: ${inactivePlayers.map(p => p.name).join(', ')}`);
    }
  }

  /**
   * 检查配对是否重复
   */
  private static isDuplicatePairing(pairing: Pairing, existingPairings: Pairing[]): boolean {
    const signature = this.getPairingSignature(pairing);
    return existingPairings.some(existing => 
      this.getPairingSignature(existing) === signature
    );
  }

  /**
   * 计算配对频率
   */
  private static calculatePairingFrequency(players: Player[], history: Pairing[]): Map<string, number> {
    const frequency = new Map<string, number>();

    // 初始化所有可能的配对组合
    for (let i = 0; i < players.length; i++) {
      for (let j = i + 1; j < players.length; j++) {
        const key = [players[i].id, players[j].id].sort().join('-');
        frequency.set(key, 0);
      }
    }

    // 统计历史配对频率
    history.forEach(pairing => {
      // 统计team1内部配对
      const team1Key = pairing.team1.map(p => p.id).sort().join('-');
      frequency.set(team1Key, (frequency.get(team1Key) || 0) + 1);

      // 统计team2内部配对
      const team2Key = pairing.team2.map(p => p.id).sort().join('-');
      frequency.set(team2Key, (frequency.get(team2Key) || 0) + 1);
    });

    return frequency;
  }

  /**
   * 生成所有可能的配对组合
   */
  private static generateAllPossiblePairings(players: Player[]): Pairing[] {
    const pairings: Pairing[] = [];
    
    // 生成所有可能的4人配对方式
    // 对于4个人A,B,C,D，可能的配对有：
    // 1. (A,B) vs (C,D)
    // 2. (A,C) vs (B,D)  
    // 3. (A,D) vs (B,C)
    
    const [p1, p2, p3, p4] = players;
    
    pairings.push({
      team1: [p1, p2] as [Player, Player],
      team2: [p3, p4] as [Player, Player],
      createdAt: new Date(),
    });
    
    pairings.push({
      team1: [p1, p3] as [Player, Player],
      team2: [p2, p4] as [Player, Player],
      createdAt: new Date(),
    });
    
    pairings.push({
      team1: [p1, p4] as [Player, Player],
      team2: [p2, p3] as [Player, Player],
      createdAt: new Date(),
    });

    return pairings;
  }

  /**
   * 计算配对的平衡分数（分数越低越平衡）
   */
  private static calculatePairingScore(pairing: Pairing, frequency: Map<string, number>): number {
    const team1Key = pairing.team1.map(p => p.id).sort().join('-');
    const team2Key = pairing.team2.map(p => p.id).sort().join('-');
    
    const team1Frequency = frequency.get(team1Key) || 0;
    const team2Frequency = frequency.get(team2Key) || 0;
    
    // 返回两个队伍配对频率的总和
    return team1Frequency + team2Frequency;
  }
}